import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageSquare,
  Sparkles,
  MessageCircle,
  Trash2,
  Edit3,
  Flower2,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useToggleBloomScore,
} from "@/hooks/useApi";
import {
  Comment as CommentType,
  CreateCommentRequest,
  UpdateCommentRequest,
} from "@/lib/campaignsApi";
import { toast } from "@/hooks/use-toast";

interface CommentThreadProps {
  comments: CommentType[];
  campaignId: string;
  onCommentsChange?: () => void;
  className?: string;
}

const CommentThread: React.FC<CommentThreadProps> = ({
  comments,
  campaignId,
  onCommentsChange,
  className,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  const createCommentMutation = useCreateComment();
  const updateCommentMutation = useUpdateComment();
  const deleteCommentMutation = useDeleteComment();
  const toggleBloomScoreMutation = useToggleBloomScore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;

    try {
      const data: CreateCommentRequest = {
        content: newComment.trim(),
      };

      await createCommentMutation.mutateAsync({ campaignId, data });
      setNewComment("");
      onCommentsChange?.();
      toast({
        title: "Comment posted! 🌱",
        description: "Your thoughts have been shared with the community.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to post comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReply = async (content: string, parentId: string) => {
    if (!isAuthenticated) return;

    try {
      const data: CreateCommentRequest = {
        content: content.trim(),
        parentId,
      };

      await createCommentMutation.mutateAsync({ campaignId, data });
      onCommentsChange?.();
      toast({
        title: "Reply posted! 🌱",
        description: "Your response has been shared.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to post reply. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (commentId: string) => {
    if (!editContent.trim()) return;

    try {
      const data: UpdateCommentRequest = {
        content: editContent.trim(),
      };

      await updateCommentMutation.mutateAsync({ commentId, data });
      setEditingCommentId(null);
      setEditContent("");
      onCommentsChange?.();
      toast({
        title: "Comment updated! ✨",
        description: "Your comment has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteCommentMutation.mutateAsync(commentId);
      onCommentsChange?.();
      toast({
        title: "Comment deleted",
        description: "Your comment has been removed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBloomScore = async (commentId: string) => {
    if (!isAuthenticated) {
      toast({
        title: "Sign in required",
        description: "Please sign in to give Bloom Score to comments.",
        variant: "destructive",
      });
      return;
    }

    try {
      await toggleBloomScoreMutation.mutateAsync(commentId);
      onCommentsChange?.();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update Bloom Score. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className={cn("space-y-4 md:space-y-6", className)}>
      {/* Comment Form */}
      {isAuthenticated ? (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row sm:items-start gap-3 sm:space-x-3"
        >
          <Avatar className="self-start sm:flex">
            <AvatarFallback>
              {user?.name?.substring(0, 2).toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-3 sm:space-y-2">
            <Textarea
              placeholder="Share your thoughts, ideas, or questions about this campaign..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="min-h-20 md:min-h-24 resize-none bg-muted/30 border-2 border-primary/20 focus:border-primary/50 text-sm sm:text-base"
              disabled={createCommentMutation.isPending}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!newComment.trim() || createCommentMutation.isPending}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-sm sm:text-base px-4 sm:px-6"
              >
                {createCommentMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span className="hidden sm:inline">Posting...</span>
                    <span className="sm:hidden">Posting</span>
                  </div>
                ) : (
                  <>
                    <Sparkles className="mr-1.5 sm:mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">Share Thought</span>
                    <span className="sm:hidden">Share</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      ) : (
        <div className="text-center py-6 sm:py-8 bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl border-2 border-dashed border-primary/30">
          <Sparkles className="w-8 h-8 sm:w-12 sm:h-12 text-primary-500 mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
            Join the Discussion
          </h3>
          <p className="text-gray-600 mb-4 text-sm sm:text-base px-4 sm:px-0">
            Sign in to share your thoughts and give Bloom Score to comments.
          </p>
          <Button className="bg-primary hover:bg-primary/90 text-sm sm:text-base px-4 sm:px-6">
            Sign In to Comment
          </Button>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4 md:space-y-6">
        {comments.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🌱</div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              No thoughts shared yet
            </h3>
            <p className="text-gray-600 max-w-md mx-auto text-sm sm:text-base px-4 sm:px-0">
              Be the first to share your thoughts about this innovative idea.
              Your insights help creators bloom!
            </p>
          </div>
        ) : (
          comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              level={0}
              onReply={handleReply}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onBloomScore={handleBloomScore}
              setEditingCommentId={setEditingCommentId}
              setEditContent={setEditContent}
              editingCommentId={editingCommentId}
              editContent={editContent}
              currentUserId={user?.id}
              isAuthenticated={isAuthenticated}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface CommentItemProps {
  comment: CommentType;
  level: number;
  onReply: (content: string, parentId: string) => void;
  onEdit: (commentId: string) => void;
  onDelete: (commentId: string) => void;
  onBloomScore: (commentId: string) => void;
  setEditingCommentId: (id: string | null) => void;
  setEditContent: (content: string) => void;
  editingCommentId: string | null;
  editContent: string;
  currentUserId?: string;
  isAuthenticated: boolean;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  level,
  onReply,
  onEdit,
  onDelete,
  onBloomScore,
  setEditingCommentId,
  setEditContent,
  editingCommentId,
  editContent,
  currentUserId,
  isAuthenticated,
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState("");

  const isAuthor = currentUserId === comment.user.id;
  const canEdit = isAuthor || isAuthenticated; // In a real app, you'd check if user is campaign creator

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (replyContent.trim()) {
      onReply(replyContent, comment.id);
      setReplyContent("");
      setIsReplying(false);
    }
  };

  const handleStartEdit = () => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent("");
  };

  const handleBloomScoreClick = () => {
    onBloomScore(comment.id);
  };

  // Calculate indentation for nested replies
  const indentClass =
    level === 0
      ? ""
      : level === 1
      ? "ml-2 sm:ml-6"
      : level >= 2
      ? "ml-2 sm:ml-8"
      : "";

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:space-x-3 gap-2 sm:gap-0",
        indentClass
      )}
    >
      <Avatar className="self-start sm:flex mt-0.5 h-8 w-8">
        <AvatarImage src={comment.user.avatarUrl} />
        <AvatarFallback>
          {comment.user.name.substring(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 space-y-3 max-w-full">
        <div className="bg-muted/30 p-3 sm:p-4 rounded-lg border border-primary/10">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between mb-2 sm:mb-1 gap-1 sm:gap-2">
            <div className="font-medium text-gray-900 text-sm sm:text-base">
              {comment.user.name}
            </div>
            <div className="text-text-secondary text-xs">
              {new Date(comment.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>

          {editingCommentId === comment.id ? (
            <div className="space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-16 resize-none bg-white border-2 border-primary/20 focus:border-primary/50 text-sm sm:text-base"
              />
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  className="text-xs sm:text-sm"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => onEdit(comment.id)}
                  disabled={!editContent.trim()}
                  className="bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-text mb-2 break-words text-sm sm:text-base">
              {comment.content}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-gray-200 gap-2 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Bloom Score Button */}
              <button
                onClick={handleBloomScoreClick}
                className={cn(
                  "flex items-center text-xs sm:text-sm font-medium transition-all duration-200 hover:scale-105",
                  comment.isLiked
                    ? "text-primary-600 bg-primary-50 px-2 sm:px-3 py-1 rounded-full"
                    : "text-text-secondary hover:text-primary-600 hover:bg-primary-50 px-2 sm:px-3 py-1 rounded-full"
                )}
              >
                <Flower2
                  className={cn(
                    "w-3 h-3 sm:w-4 sm:h-4 mr-1",
                    comment.isLiked && "fill-current"
                  )}
                />
                <span className="font-semibold">{comment._count.likes}</span>
                <span className="ml-1 hidden sm:inline">Bloom Score</span>
                <span className="ml-1 sm:hidden">Score</span>
              </button>

              {/* Reply Button */}
              <button
                onClick={() => setIsReplying(!isReplying)}
                className="flex items-center text-xs sm:text-sm font-medium text-text-secondary hover:text-primary-600 hover:bg-primary-50 px-2 sm:px-3 py-1 rounded-full transition-all duration-200"
              >
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span>Reply</span>
              </button>
            </div>

            {/* Edit/Delete Actions */}
            {canEdit && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleStartEdit}
                  className="flex items-center text-xs sm:text-sm font-medium text-text-secondary hover:text-primary-600 hover:bg-primary-50 px-2 py-1 rounded transition-all duration-200"
                >
                  <Edit3 className="w-3 h-3 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => onDelete(comment.id)}
                  className="flex items-center text-xs sm:text-sm font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-all duration-200"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Reply Form */}
        {isReplying && (
          <form onSubmit={handleReplySubmit} className="space-y-2">
            <Textarea
              placeholder="Write a reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              className="min-h-16 resize-none bg-muted/30 border-2 border-primary/20 focus:border-primary/50 text-sm sm:text-base"
            />
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsReplying(false)}
                className="text-xs sm:text-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!replyContent.trim()}
                className="bg-primary hover:bg-primary/90 text-xs sm:text-sm"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Reply
              </Button>
            </div>
          </form>
        )}

        {/* Nested Replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="space-y-3 mt-3">
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                level={level + 1}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onBloomScore={onBloomScore}
                setEditingCommentId={setEditingCommentId}
                setEditContent={setEditContent}
                editingCommentId={editingCommentId}
                editContent={editContent}
                currentUserId={currentUserId}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentThread;
